import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { buInstanceLabelKeys } from "@/lib/query";

/** Matches the DB default (src/db/schema/bu-instance-label-settings.ts) so the
 * UI never flashes something different from what a fresh install actually has. */
export const DEFAULT_BU_INSTANCE_LABEL = "BU Instance";

async function extractErrorMessage(res: Response, fallback: string): Promise<string> {
  const json = await res.json().catch(() => null);
  return json?.error ?? fallback;
}

/**
 * The admin-configurable display term for "BU Instance" (Settings → System →
 * "Terminology"). Every call site across the app reads this hook instead of
 * hardcoding the string. Long `staleTime` because this changes rarely — an
 * admin edit invalidates the query immediately via
 * `useUpdateBuInstanceLabel`'s `onSuccess`, so the long staleTime just avoids
 * needless refetching, not staleness after a real change.
 *
 * Always returns a defined `label`/`labelPlural` — while loading or on fetch
 * failure, falls back to `DEFAULT_BU_INSTANCE_LABEL` so the UI never renders
 * "undefined". `labelPlural` is the one place the naive `${label}s` rule
 * lives — callers needing a plural should read it from here, not append "s"
 * themselves.
 */
export function useBuInstanceLabel() {
  const { data } = useQuery({
    queryKey: buInstanceLabelKeys.all,
    queryFn: async (): Promise<{ label: string }> => {
      const res = await fetch("/api/bu-instance-label");
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to fetch label"));
      return res.json();
    },
    staleTime: 60 * 60 * 1000,
  });

  const label = data?.label ?? DEFAULT_BU_INSTANCE_LABEL;
  return { label, labelPlural: `${label}s` };
}

/** Admin-only mutation, backing the System settings tab's form. */
export function useUpdateBuInstanceLabel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (label: string): Promise<{ label: string }> => {
      const res = await fetch("/api/admin/bu-instance-label", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label }),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to update label"));
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: buInstanceLabelKeys.all });
      toast.success("Label saved");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update label");
    },
  });
}
